import { FolderClosed, Users2, FileX, File } from "lucide-react";
import { VerticalMenu } from "../../components/Menu/Vertical-menu";
import { PDFCard } from "../../components/pdfCard/pdfCard";
import img from "../../images/media.png";
import "../../style/carouselPerView.css";
import { useEffect, useState } from "react";
import { getAllPaginatedActs } from "../../API/Mediatheque/Administration/actService";
import { useScrollToTop } from "../../hooks/hooks";
import pdfImg from "../../images/icons/pdf.png";

const MediatheuqeAdministration = () => {
  const menuData = [
    {
      title: "décisions",
      icon: FolderClosed,
      links: [],
    },
    {
      title: "comité",
      icon: FolderClosed,
      links: [
        { title: "Ordre du jour", href: "#" },
        { title: "PV", href: "#" },
        { title: "Délibérations", href: "#" },
        { title: "Liste des Délibérations", href: "#" },
      ],
    },
    {
      title: "bureau",
      icon: Users2,
      links: [
        { title: "Ordre du jour", href: "#" },
        { title: "PV", href: "#" },
        { title: "Délibérations", href: "#" },
        { title: "Liste des Délibérations", href: "#" },
      ],
      subMenuItems: [],
    },
    {
      title: "arretes",
      icon: FolderClosed,
      links: [],
      subMenuItems: [],
    },
  ];

  const [acts, setActs] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState(menuData[0].title);
  const [activeSubCategory, setSubActiveCategory] = useState("");

  const [totalPages, setTotalPages] = useState(1);

  // Get the current active menu item
  const getCurrentMenuItem = () => {
    return menuData.find((item) => item.title === activeCategory);
  };

  // Check if current category has subcategories
  const hasSubCategories = () => {
    const currentItem = getCurrentMenuItem();
    return currentItem && currentItem.links && currentItem.links.length > 0;
  };

  // Check if we should show acts (either no subcategories or a subcategory is selected)
  const shouldShowActs = () => {
    return !hasSubCategories() || activeSubCategory !== "";
  };

  const fetchData = async () => {
    // Only fetch data if we should show acts
    if (!shouldShowActs()) return;

    const subCategory = encodeURIComponent(activeSubCategory.toLowerCase());
    const category = encodeURIComponent(activeCategory.toLowerCase());
    const response = await getAllPaginatedActs(page, category, subCategory);
    setActs(response?.acts || []);
    setTotalPages(response?.totalPages || 0);
  };

  useEffect(() => {
    // Reset subcategory when category changes
    setSubActiveCategory("");
    setPage(1);
  }, [activeCategory]);

  useEffect(() => {
    // Reset page when subcategory changes
    setPage(1);
  }, [activeSubCategory]);

  useEffect(() => {
    fetchData();
  }, [page, activeCategory, activeSubCategory]);

  useScrollToTop();

  const renderSubCategoriesList = () => {
    const currentItem = getCurrentMenuItem();
    if (!currentItem || !currentItem.links) return null;

    return (
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {currentItem.links.map((link, index) => (
          <div
            key={index}
            onClick={() => setSubActiveCategory(link.title)}
            className="p-6 transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:border-primary"
          >
            <div className="flex items-center space-x-4">
              <img src={pdfImg} alt="PDF" className="object-contain w-8 h-8" />
              <h3 className="text-lg font-semibold text-gray-900">
                {link.title}
              </h3>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (!shouldShowActs()) {
      // Show subcategories list
      return (
        <div className="min-h-[400px]">
          <div className="mb-2">
            <h3 className="text-lg font-medium text-gray-700 ">
              Sélectionnez une sous-catégorie:
            </h3>
          </div>
          {renderSubCategoriesList()}
        </div>
      );
    }

    // Show acts
    if (totalPages === 0) {
      return (
        <div className="min-h-[400px]">
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
            <div className="p-6 mb-6 bg-gray-100 rounded-full">
              <FileX className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Aucun fichier disponible
            </h3>
            <p className="max-w-md text-gray-500">
              Il n'y a actuellement aucun document disponible dans cette
              catégorie. Veuillez vérifier plus tard ou sélectionner une autre
              catégorie.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-[400px]">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {acts.map((doc, index) => (
            <PDFCard
              file={doc.file}
              key={index}
              title={doc.title}
              size={doc.size}
              depositDate={doc.date ? new Date(doc.date).toLocaleDateString("fr-FR") : "Non définie"}
              modificationDate={new Date(doc.updatedAt).toLocaleDateString(
                "fr-FR"
              )}
            />
          ))}
        </div>
      </div>
    );
  };

  const getDisplayTitle = () => {
    if (activeSubCategory) {
      return `${activeCategory.toUpperCase()} - ${activeSubCategory}`;
    }
    return activeCategory.toUpperCase();
  };

  return (
    <div className="mb-52">
      {/* Hero Section */}
      <section
        className="p-6 text-center sm:p-10 lg:p-32 bg-black/70 bg-blend-overlay"
        style={{
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <h1 className="text-2xl font-bold text-center text-white sm:text-3xl md:text-4xl lg:text-7xl">
          Médiathèque
        </h1>
        <p className="mt-3 text-sm text-white sm:text-base">
          Toutes nos documentations à votre disposition
        </p>
      </section>

      {/* Content Section */}
      <div className="flex flex-col gap-4 px-4 mt-6 sm:px-6 md:px-8 lg:px-12 xl:px-16 md:gap-8 lg:gap-12 md:flex-row">
        {/* Vertical Menu */}
        <div className="w-full md:w-1/4 lg:w-1/5">
          <VerticalMenu
            items={menuData}
            setActiveCategory={setActiveCategory}
            setSubActiveCategory={setSubActiveCategory}
            subActiveCategory={activeSubCategory}
          />
        </div>

        {/* Main Content */}
        <div className="w-full md:w-3/4 lg:w-4/5">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-lg font-semibold sm:text-xl lg:text-2xl">
              {getDisplayTitle()}
            </h2>
            {activeSubCategory && (
              <button
                onClick={() => setSubActiveCategory("")}
                className="px-3 py-1 text-sm transition-colors border rounded-full text-primary border-primary hover:bg-primary hover:text-white"
              >
                ← Retour
              </button>
            )}
          </div>

          {/* Content */}
          {renderContent()}

          {/* Pagination Controls */}
          {shouldShowActs() && totalPages > 1 && (
            <div className="flex flex-col items-center justify-center mt-8 space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-medium transition-colors ${
                  page === 1
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-primary text-white hover:bg-primary-dark"
                }`}
              >
                Précédent
              </button>
              <span className="px-4 py-2 text-sm font-medium bg-white border rounded">
                {page} / {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-medium transition-colors ${
                  page === totalPages
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-primary text-white hover:bg-primary-dark"
                }`}
              >
                Suivant
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediatheuqeAdministration;
